import React, { useEffect, useRef, useState } from 'react'
import { EmailPreferences, I2FaSetup, IUserObject } from 'types'
import api from 'helpers/api'
import { useAuth } from 'hooks/auth'
import { useApplicationStore } from 'hooks/application'
import Button from 'components/button'
import PageHeader from 'components/pageHeader'
import Toggle from 'components/toggle'
import { InlineRow, SETTINGS_CANVAS, SettingsCard, WhyDisabled } from 'views/settings/components/settingsCard'
import ChangePasswordDialog from 'views/security/components/changePasswordDialog'
import AccountRows, { AccountFooter, UpgradeFooter } from 'views/security/components/accountRows'
import DevicesCard from 'views/security/components/devicesCard'
import SupportAccess from 'views/security/components/supportAccess'
import { TurnOffTwoFactorDialog, TurnOnTwoFactorDialog } from 'views/security/components/twoFactorDialogs'

/** The emails that can be turned off. On means it's sent: the stored flag is an opt-out. */
const EMAILS: Array<{ key: keyof EmailPreferences; label: string; description?: string }> = [
  {
    key: 'optOutMentionEmail',
    label: 'Daily digest',
    description:
      'One email at 9am UK time with unread comments: mentions, replies, and conversations that are resolved or reopened. It is not sent when there is nothing new.'
  },
  { key: 'optOutWelcomeEmail', label: 'Welcome emails' },
  { key: 'optOutSyncEmail', label: 'Sync emails' },
  { key: 'optOutAiEmail', label: 'AI emails' },
  { key: 'optOutHubEmail', label: 'Hub emails' },
  { key: 'optOutDuplicationEmail', label: 'Duplication emails' },
  { key: 'optOutDeprecationEmail', label: 'Deprecation emails' },
  { key: 'optOutMarketingEmail', label: 'Marketing emails' },
  { key: 'optOutCommunityEmail', label: 'Community emails' }
]

/**
 * The account's settings and security: its email and plan, how it signs in (password, multi-factor
 * authentication, Morta Support access), the devices signed in to it, and the emails it gets. Each change saves straight away
 * or in its own dialog.
 */
const Security: React.FC = () => {
  const { user, updateUser } = useAuth()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const [me, setMe] = useState<IUserObject>()
  const [changingPassword, setChangingPassword] = useState(false)
  const [twoFactorSetup, setTwoFactorSetup] = useState<I2FaSetup>()
  const [turningOffTwoFactor, setTurningOffTwoFactor] = useState(false)
  // Setting up MFA makes the server a new secret: one request at a time, so the QR code shown is the one the
  // code will be checked against.
  const [startingTwoFactor, setStartingTwoFactor] = useState(false)
  const starting = useRef(false)
  // The email preferences as last chosen, until the account holds them: the switches show them, and a switch
  // flipped meanwhile builds on them. Saves go one at a time, the latest choice after the one under way, so an
  // earlier save can never land after a later one.
  const [chosenPreferences, setChosenPreferences] = useState<EmailPreferences | null>(null)
  const chosen = useRef<EmailPreferences | null>(null)
  const savingPreferences = useRef(false)
  const queuedPreferences = useRef<EmailPreferences | null>(null)

  useEffect(() => {
    document.title = 'Settings & Security'
  }, [])

  useEffect(() => {
    api
      .getMe()
      .then((response) => setMe(response.data))
      .catch(() => undefined)
  }, [])

  // The switch stays as it is until the dialog it opens has turned MFA on or off.
  const startTwoFactor = async () => {
    if (starting.current) return
    starting.current = true
    setStartingTwoFactor(true)
    let setup: I2FaSetup
    try {
      const response = await api.setup2FA()
      setup = response.data
    } catch (error) {
      starting.current = false
      setStartingTwoFactor(false)
      displayErrorMessage(error)
      return
    }
    starting.current = false
    setStartingTwoFactor(false)
    setTwoFactorSetup(setup)
  }

  const choose = (preferences: EmailPreferences | null) => {
    chosen.current = preferences
    setChosenPreferences(preferences)
  }

  const savePreferences = (preferences: EmailPreferences) => {
    if (savingPreferences.current) {
      queuedPreferences.current = preferences
      return
    }
    savingPreferences.current = true
    api
      .updateEmailPreferences(preferences)
      .then(async () => {
        await updateUser(preferences)
        // Saved, and nothing chosen since: the account holds them now.
        if (chosen.current === preferences) {
          choose(null)
          setSnackbarMessage({ status: 'success', message: 'Email preferences were updated' })
        }
      })
      .catch((error) => {
        // Not saved: the switches go back to the account as it is, unless a later choice is still to be sent.
        if (!queuedPreferences.current) choose(null)
        displayErrorMessage(error)
      })
      .then(() => {
        savingPreferences.current = false
        const next = queuedPreferences.current
        queuedPreferences.current = null
        if (next) savePreferences(next)
      })
  }

  // Every preference is sent, so changing one can never reset the others.
  const setEmailPreference = (overrides: EmailPreferences) => {
    if (!user) return
    const preferences: EmailPreferences = {
      ...(chosen.current ?? {
        optOutWelcomeEmail: user.optOutWelcomeEmail,
        optOutSyncEmail: user.optOutSyncEmail,
        optOutAiEmail: user.optOutAiEmail,
        optOutHubEmail: user.optOutHubEmail,
        optOutDuplicationEmail: user.optOutDuplicationEmail,
        optOutDeprecationEmail: user.optOutDeprecationEmail,
        optOutMentionEmail: user.optOutMentionEmail,
        optOutAssignmentEmail: user.optOutAssignmentEmail,
        optOutMarketingEmail: user.optOutMarketingEmail,
        optOutCommunityEmail: user.optOutCommunityEmail
      }),
      ...overrides
    }
    choose(preferences)
    savePreferences(preferences)
  }

  // A free account is invited to upgrade; one with a team's seat is told who manages it.
  const free = me?.subscriptionLevel === 0
  const accountFooter = () => {
    if (free && me) return <UpgradeFooter me={me} />
    if (me?.team) return <AccountFooter me={me} />
    return undefined
  }

  // Signing out clears the user while this page can still draw once before the move to sign in.
  if (!user) return null

  return (
    <div className="@container flex h-full w-full flex-col">
      <PageHeader parent="Account" name="Settings & Security" testIdPrefix="security" />
      <div className={`min-h-0 flex-1 overflow-auto border-t border-divider ${SETTINGS_CANVAS}`}>
        <div className="mx-auto flex max-w-[720px] flex-col gap-4">
          <SettingsCard title="Account" footer={accountFooter()} footerTone={free ? 'blue' : 'sunken'}>
            <InlineRow label="Email">
              <span className="font-medium text-body">{user.email}</span>
            </InlineRow>
            {me && <AccountRows me={me} />}
          </SettingsCard>

          <SettingsCard
            title="Security"
            description="How this account signs in to Morta, and who else can."
            footer={<SupportAccess />}
          >
            <InlineRow
              label="Password"
              description={
                user.hasPassword
                  ? 'Used to sign in with the email address above.'
                  : 'This account signs in with Google, Microsoft or single sign-on, so it has no Morta password.'
              }
            >
              {user.hasPassword && (
                <Button
                  internalType="outline"
                  onClick={() => setChangingPassword(true)}
                  data-cy="change-password-button"
                >
                  Change Password
                </Button>
              )}
            </InlineRow>

            <InlineRow
              label="Multi-factor authentication (MFA)"
              description="Signing in needs the six-digit code from an authenticator app as well as the password."
            >
              <WhyDisabled
                reason={
                  user.hasPassword
                    ? undefined
                    : "Available to accounts that sign in with a password. Accounts that sign in with Google, Microsoft or single sign-on use their provider's MFA."
                }
              >
                <Toggle
                  aria-label="Multi-factor authentication (MFA)"
                  toggled={!!user.is2FaEnabled}
                  disabled={!user.hasPassword || startingTwoFactor}
                  onToggle={(toggled) => (toggled ? startTwoFactor() : setTurningOffTwoFactor(true))}
                />
              </WhyDisabled>
            </InlineRow>
          </SettingsCard>

          <DevicesCard />

          <SettingsCard title="Emails">
            {EMAILS.map(({ key, label, description }) => (
              <InlineRow key={key} label={label} description={description}>
                <Toggle
                  aria-label={label}
                  toggled={!(chosenPreferences ?? user)[key]}
                  onToggle={(toggled) => setEmailPreference({ [key]: !toggled })}
                />
              </InlineRow>
            ))}
          </SettingsCard>
        </div>
      </div>

      {changingPassword && <ChangePasswordDialog onClose={() => setChangingPassword(false)} />}
      {twoFactorSetup && <TurnOnTwoFactorDialog setup={twoFactorSetup} onClose={() => setTwoFactorSetup(undefined)} />}
      {turningOffTwoFactor && <TurnOffTwoFactorDialog onClose={() => setTurningOffTwoFactor(false)} />}
    </div>
  )
}

export default Security
